import { ENVIRONMENTS, PLATFORMS, RESERVED_TAG_KEYS } from '../types';
export function validateConfig(config) {
    const errors = [];
    const requiredStrings = [
        { field: 'service', label: 'service' },
        { field: 'version', label: 'version' },
        { field: 'applicationId', label: 'applicationId' },
        { field: 'clientToken', label: 'clientToken' },
    ];
    for (const { field, label } of requiredStrings) {
        const value = config[field];
        if (typeof value !== 'string' || value.trim() === '') {
            errors.push(`"${label}" is required and must be a non-empty string`);
        }
    }
    // Optional country. When provided, must be a non-empty string — passing an
    // empty string would emit a literal empty `country` tag to Datadog and break
    // any dashboard filtered on country.
    if (config.country !== undefined) {
        if (typeof config.country !== 'string' || config.country.trim() === '') {
            errors.push('"country" must be a non-empty string when provided. Omit the field for apps not tied to a single country.');
        }
    }
    // Optional store identifier. Constraining to non-negative integers blocks
    // accidental strings, NaN, or floats that would otherwise reach Datadog as
    // arbitrary tag values and break dashboards filtered on `location_id`.
    if (config.locationId !== undefined) {
        const id = config.locationId;
        if (typeof id !== 'number' || !Number.isInteger(id) || id < 0) {
            errors.push(`"locationId" must be a non-negative integer when provided. Received: ${String(id)}`);
        }
    }
    if (!ENVIRONMENTS.includes(config.env)) {
        errors.push(`"env" must be one of: ${ENVIRONMENTS.join(', ')}. Received: "${String(config.env)}"`);
    }
    if (!PLATFORMS.includes(config.platform)) {
        errors.push(`"platform" must be one of: ${PLATFORMS.join(', ')}. Received: "${String(config.platform)}"`);
    }
    // Sample rates are documented as percentages [0, 100]. Without this guard a
    // NaN, negative, or out-of-range value reaches the Datadog SDK and surfaces
    // as a runtime warning instead of a config-time failure.
    const optionalSampleRates = [
        { field: 'sessionSampleRate', label: 'sessionSampleRate' },
        { field: 'sessionReplaySampleRate', label: 'sessionReplaySampleRate' },
        { field: 'logsSampleRate', label: 'logsSampleRate' },
    ];
    for (const { field, label } of optionalSampleRates) {
        const value = config[field];
        if (value === undefined)
            continue;
        if (typeof value !== 'number' ||
            !Number.isFinite(value) ||
            value < 0 ||
            value > 100) {
            errors.push(`"${label}" must be a finite number between 0 and 100. Received: ${String(value)}`);
        }
    }
    if (config.additionalTags) {
        const reservedConflicts = Object.keys(config.additionalTags).filter((key) => RESERVED_TAG_KEYS.includes(key));
        if (reservedConflicts.length > 0) {
            errors.push(`"additionalTags" cannot reuse reserved keys: ${reservedConflicts.join(', ')}. ` +
                `These tags are owned by the package and set from the validated config: ${RESERVED_TAG_KEYS.join(', ')}`);
        }
    }
    if (errors.length > 0) {
        throw new Error(`Datadog configuration validation failed:\n- ${errors.join('\n- ')}`);
    }
}
